import * as Sentry from '@sentry/react';
import { getAuthToken } from '@core/api';
import { captureAuthError } from '@core/sentry/utils';
import { capturePaymentError } from '@core/sentry';
import { shouldBypassGuarantee } from '@features/booking/utils/guaranteeBypass';
import { isPaymentGuaranteeType } from '@features/booking/state/types';
import {
  CommitError,
  buildBasePayload,
  commitEarlyAccess,
  commitTraditional,
  commitDepositStripe,
  commitDepositMercadoPago,
  getDisplayCurrency,
} from './commit';
import type { CommitContext, CommitDerived, MpFlowFlagBox } from './commit';

/**
 * Outcome enum returned by guard evaluation. Lets the entry point understand
 * which guard fired without exposing implementation details to the caller.
 */
type GuardOutcome = 'continue' | 'guard-rejected';

/**
 * Result of a commit attempt. The hook uses this to decide whether to
 * un-flicker the submit button (guard path) or let the success/error path
 * govern (work was actually attempted).
 */
export type CommitOutcome =
  | 'success'        // API call made and persisted
  | 'guarded';       // a guard fired before any API call — caller may want to skip side effects

/**
 * Pre-flight guards — exact behavior preservation from handleConfirm
 * guards. Each guard either calls a navigation side effect (goToStep or
 * window.dispatchEvent) and returns 'guard-rejected', returns the same
 * sentinel for silent rejections (missing required fields), or returns
 * 'continue'.
 *
 * Sentry breadcrumb category: 'confirmation-guard' (for auth + payment guards).
 */
function runGuards(ctx: CommitContext, derived: CommitDerived): GuardOutcome {
  const { booking, goToStep } = ctx;
  const { isWaitingList, bypassesGuarantee } = derived;

  // Already-reserved bypass: ConfirmationPage may render again after backnav
  // from /booking-final; never re-fire the grab endpoint (would be blocked by
  // 412 concurrent_post anyway).
  if (booking.reservationId) {
    goToStep('BOOKING_FINAL');
    return 'guard-rejected';
  }

  // Silent rejection: missing date/time (standard) or wlDates (waiting list).
  if (!isWaitingList && (!booking.selectedDate || !booking.selectedTime)) return 'guard-rejected';
  if (isWaitingList && booking.wlDates.length === 0) return 'guard-rejected';

  // Defensive auth-token guard: storage was cleared mid-flow (ITP eviction,
  // manual tampering, WebView quota wipe). We cannot use goToStep('SIGN_IN')
  // because SIGN_IN is only in activeSteps when ctx.isAuthenticated === false,
  // but in this failure mode React state still says authenticated. Dispatching
  // auth:expired triggers AuthProvider.logout() which fixes the flow state.
  // Agent reservations are exempt (they sign in as the restaurant).
  // Sentry breadcrumb category: 'confirmation-guard'.
  if (!booking.isAgentReservation && !getAuthToken()) {
    Sentry.addBreadcrumb({
      category: 'confirmation-guard',
      message: 'Token missing at confirmation, triggering auth:expired',
      level: 'warning',
    });
    captureAuthError('auth.confirmation_no_token', {
      isWaitingList,
      flowType: booking.flowType,
    });
    window.dispatchEvent(
      new CustomEvent('auth:expired', {
        detail: { from: window.location.pathname },
      }),
    );
    return 'guard-rejected';
  }

  // Agent validation: ensure guest info was collected.
  if (booking.isAgentReservation && (!booking.guestName || !booking.guestEmail || !booking.guestPhone)) {
    goToStep('AGENT_GUEST_INFO');
    return 'guard-rejected';
  }

  // Payment guard: a payment-bearing guarantee requires a card token.
  // Without this guard, posting cardToken=null reaches the backend and
  // crashes with TypeError in processCardVerification.
  // Sentry breadcrumb category: 'confirmation-guard'.
  if (
    !booking.isAgentReservation &&
    !bypassesGuarantee &&
    isPaymentGuaranteeType(booking.guaranteeType) &&
    booking.feeDeposit > 0 &&
    !booking.cardToken
  ) {
    Sentry.addBreadcrumb({
      category: 'confirmation-guard',
      message: 'Missing cardToken for payment guarantee, redirecting to GUARANTEE',
      level: 'warning',
      data: {
        guaranteeType: booking.guaranteeType,
        feeDeposit: booking.feeDeposit,
        isWaitingList,
      },
    });
    goToStep('GUARANTEE');
    return 'guard-rejected';
  }

  return 'continue';
}

/**
 * Pre-derive shared values that every path needs. Computed once at entry.
 */
function deriveCommitState(ctx: CommitContext): CommitDerived {
  const { booking, restaurant, authState } = ctx;

  const isWaitingList = booking.flowType === 'waiting_list';

  // Bypass: gift card or reschedule of a guaranteed reservation.
  // Same predicate as useBookingFlow.buildFlowContext + calculateFee.
  const bypassesGuarantee = shouldBypassGuarantee({
    hasGiftCard: !!booking.giftCardToken,
    rescheduleOriginalType: booking.rescheduleId ? booking.rescheduleOriginalType : null,
  });

  // A slot can be typed no_show/deposit purely to surface the card icon in the
  // time grid because SOME menus on it require a guarantee. When the SELECTED
  // menu defines no guarantee of its own (e.g. à la carte: guarantee.type ''),
  // calculateFee resolves the type from the slot but with a 0 fee, so
  // booking.feeDeposit === 0: there is nothing to charge and no card is
  // collected (GUARANTEE step was skipped — isGuaranteeRequired gates on
  // guaranteeFee > 0). Committing such a reservation on a processor-specific
  // endpoint is wrong: it routes to deposit-grab-stripe (paymentProcessor was
  // never set) and an MP restaurant's backend rejects it with "Type Action
  // difiere de restaurant" (Sentry MEITRE-CORE-R46). Treat a zero-fee guarantee
  // as traditional — mirroring isPaymentGuarantee()/isGuaranteeRequired(), which
  // already gate the payment step on fee > 0. Scoped to the standard flow; WL
  // keeps its own guarantee/endpoint handling (different controller).
  const isZeroFeeStandardGuarantee = !isWaitingList && booking.feeDeposit <= 0;

  // Agent / gift card / reschedule bypass (and zero-fee standard reservations):
  // use traditional-grab regardless of booking.guaranteeType (which may carry a
  // stale value from an earlier step or from the WL rules API).
  const effectiveGuaranteeType = booking.isAgentReservation || bypassesGuarantee || isZeroFeeStandardGuarantee
    ? 'traditional'
    : booking.guaranteeType;

  // Guest info: for agent reservations use the guest data collected in
  // AgentGuestInfoPage; otherwise use the authenticated user's profile.
  const guestName = booking.isAgentReservation
    ? booking.guestName
    : (authState.name || booking.guestName || 'Guest');
  const guestEmail = booking.isAgentReservation
    ? booking.guestEmail
    : (authState.email || booking.guestEmail);
  const guestPhone = booking.isAgentReservation
    ? booking.guestPhone
    : (authState.phone || booking.guestPhone);

  return {
    isWaitingList,
    bypassesGuarantee,
    effectiveGuaranteeType,
    guestName,
    guestEmail,
    guestPhone,
    displayCurrency: getDisplayCurrency(restaurant),
  };
}

/**
 * Entry point for the reservation commit flow.
 *
 * Behavior contract:
 *
 * 1. Runs preflight guards (auth, agent, payment). Each guard either
 *    triggers navigation and returns; on any rejection commitReservation
 *    returns CommitOutcome 'guarded' so the caller can skip post-commit
 *    UI state changes (notably: avoid the isSubmitting flicker on
 *    rejection).
 * 2. On 'continue', derives shared state and dispatches to a per-path
 *    module based on (isEarlyAccessFlow, effectiveGuaranteeType,
 *    paymentProcessor).
 * 3. Each path performs API call(s) + state writes + navigation. State
 *    write order is invariant: setReservationResult → setGuestInfo →
 *    fireConversion → invalidateTimeSlots → goToNext.
 * 4. On error, Sentry captures via capturePaymentError and throws CommitError
 *    (which carries mpFlow3dsLoaded + processor for the caller to map to UI).
 *
 * Does NOT touch React state — the caller (useReservationCommit hook) owns
 * isSubmitting / error / requireReload.
 */
export async function commitReservation(
  ctx: CommitContext,
  onCommitStart?: () => void,
): Promise<CommitOutcome> {
  const derived = deriveCommitState(ctx);

  const guardResult = runGuards(ctx, derived);
  if (guardResult !== 'continue') return 'guarded';

  // Guards passed — signal the caller (hook) that real work is starting so
  // it can flip isSubmitting. Original handleConfirm called setIsSubmitting(true)
  // here at line 270 of the pre-extraction code. The callback lets the hook
  // own the React state while commitReservation owns the lifecycle event.
  onCommitStart?.();

  // Caller-owned flag-box so the MP path's `mpFlow3dsLoaded` survives a throw.
  // See MpFlowFlagBox doc for why a local `let` inside commitDepositMercadoPago
  // would be invisible to this outer catch on the throw path.
  const flagBox: MpFlowFlagBox = { mpFlow3dsLoaded: false };

  // basePayload construction is INSIDE the try so any throw from
  // getUtmParams / getCapturedReferrer / calculateReservationSource
  // (e.g., corrupt localStorage) is caught and routed through
  // capturePaymentError + CommitError, not escaped unwrapped.
  try {
    const basePayload = buildBasePayload(ctx, derived);

    if (ctx.booking.isEarlyAccessFlow) {
      await commitEarlyAccess(ctx, derived, basePayload);
      return 'success';
    }

    if (derived.effectiveGuaranteeType === 'deposit' || derived.effectiveGuaranteeType === 'no_show') {
      // Source of truth is the restaurant's configured processor, NOT
      // booking.paymentProcessor. The latter is a state mirror set only as a
      // side effect of mounting the payment form, so it is null whenever the
      // form was skipped — exactly how an MP restaurant ended up POSTing to
      // /deposit-grab-stripe (MEITRE-CORE-R46). Form selection (PaymentPage)
      // and the legacy app both key off restaurant.paymentProcessor; the commit
      // must use the same source. Covers standard grab AND waiting list (both
      // dispatch through here). Request-guarantee (ConfirmGuaranteePage) is a
      // separate flow on an already-created reservation and routes itself.
      const isMercadoPago = ctx.restaurant.paymentProcessor === 'mercadopago';
      if (isMercadoPago) {
        await commitDepositMercadoPago(ctx, derived, basePayload, flagBox);
      } else {
        await commitDepositStripe(ctx, derived, basePayload);
      }
    } else {
      await commitTraditional(ctx, derived, basePayload);
    }
    return 'success';
  } catch (err) {
    const processor = ctx.restaurant.paymentProcessor === 'mercadopago' ? 'mercadopago' : 'stripe';
    capturePaymentError(err, processor, {
      guaranteeType: ctx.booking.guaranteeType,
      isWaitingList: derived.isWaitingList,
      diners: ctx.booking.diners,
      restaurant: ctx.restaurant.id,
    });
    throw new CommitError(err, flagBox.mpFlow3dsLoaded, processor);
  }
}
